import {
  loginRequestSchema,
  type FederatedProviderSlug,
  type SignInClient,
} from '@instanexus/contracts';
import type { ApiClient } from '@instanexus/sdk';

import { saveFederatedReturn } from '@/lib/federated-return';

/**
 * Continue with <provider> on the central sign-in page. The typed email becomes the provider's
 * login hint only when it is a valid address; `tenantSlug` is sent only when non-empty. Saves the
 * return (so the callback can resume this sign-in), starts, and leaves for the provider.
 * `/start` rejections propagate to the caller's alert.
 */
export async function startProviderSignIn(
  api: Pick<ApiClient, 'federation'>,
  provider: FederatedProviderSlug,
  input: { readonly search: string; readonly email: string; readonly tenantSlug?: string },
  redirectTo: (url: string) => void,
  storage?: Storage,
): Promise<void> {
  const email = input.email.trim();
  const tenantSlug = (input.tenantSlug ?? '').trim();
  const loginHint = loginRequestSchema.shape.email.safeParse(email).success ? email : undefined;
  saveFederatedReturn(
    {
      kind: 'sign_in',
      search: input.search,
      ...(loginHint === undefined ? {} : { loginHint }),
    },
    storage,
  );
  const { authorizeUrl } = await api.federation.start(provider, {
    ...(loginHint === undefined ? {} : { loginHint }),
    ...(tenantSlug.length > 0 ? { tenantSlug } : {}),
  });
  redirectTo(authorizeUrl);
}

/** Accept with <provider> on the central accept page: saves an 'invite' return, starts the invite round trip, leaves. */
export async function startProviderInvite(
  api: Pick<ApiClient, 'federation'>,
  provider: FederatedProviderSlug,
  input: { readonly client: SignInClient; readonly email: string; readonly token: string },
  redirectTo: (url: string) => void,
  storage?: Storage,
): Promise<void> {
  // The invitation token goes only into the request body; the saved return never holds it.
  saveFederatedReturn({ kind: 'invite', client: input.client, loginHint: input.email }, storage);
  const { authorizeUrl } = await api.federation.startInvite(provider, {
    email: input.email,
    token: input.token,
  });
  redirectTo(authorizeUrl);
}
